import { useState } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { useAuth } from '@/context/auth-context';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  Home,
  ClipboardList,
  Users,
  Package,
  FileText,
  BookOpen,
  Settings,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  MoreHorizontal,
  Shield,
  Boxes,
  Receipt,
  CreditCard,
  Factory,
  Wifi,
  WifiOff,
  Bell,
} from 'lucide-react';
import { useEvents } from '@/hooks/use-events';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AppWalkthrough } from '@/components/AppWalkthrough';
import { NotificationBell } from '@/components/notifications/NotificationBell';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: Home },
  { label: 'Orders', href: '/orders', icon: ClipboardList },
  { label: 'Production', href: '/production', icon: Factory },
  { label: 'Customers', href: '/customers', icon: Users },
  { label: 'Services', href: '/services', icon: Package },
  { label: 'Inventory', href: '/inventory', icon: Boxes },
  { label: 'Expenses', href: '/expenses', icon: Receipt },
  { label: 'Payments', href: '/payments', icon: CreditCard },
  { label: 'Reports', href: '/reports', icon: FileText },
  { label: 'Accounting', href: '/accounting', icon: BookOpen },
  { label: 'Users', href: '/users', icon: Shield, adminOnly: true },
  { label: 'Settings', href: '/settings', icon: Settings, adminOnly: true },
];

// The 4 primary tabs shown in the bottom bar on mobile
const primaryMobileNav: NavItem[] = [
  { label: 'Home', href: '/', icon: Home },
  { label: 'Orders', href: '/orders', icon: ClipboardList },
  { label: 'Production', href: '/production', icon: Factory },
  { label: 'Customers', href: '/customers', icon: Users },
];

// Secondary items shown in the "More" sheet
const secondaryMobileNav: NavItem[] = [
  { label: 'Services', href: '/services', icon: Package },
  { label: 'Inventory', href: '/inventory', icon: Boxes },
  { label: 'Expenses', href: '/expenses', icon: Receipt },
  { label: 'Accounting', href: '/accounting', icon: BookOpen },
  { label: 'Users', href: '/users', icon: Shield, adminOnly: true },
  { label: 'Settings', href: '/settings', icon: Settings, adminOnly: true },
];

/**
 * Filter navigation items based on user role.
 * Admins/Owners get everything. Cashiers/Staff might be restricted.
 */
function getFilteredNavItems(items: NavItem[], role?: string): NavItem[] {
  if (!role) return [];
  const isAdmin = role === 'admin' || role === 'owner' || role === 'manager';
  
  return items.filter(item => {
    if (item.adminOnly && !isAdmin) return false;
    
    // Example deeper role logic:
    // Cashiers usually shouldn't see Accounting/Reports unless allowed
    if (role === 'cashier' && ['/accounting', '/reports', '/services', '/inventory', '/expenses'].includes(item.href)) {
      return false;
    }
    
    return true;
  });
}

/**
 * Main application layout wrapper for authenticated routes.
 * 
 * Features:
 * 1. Responsive Sidebar (Desktop) / Bottom Navigation (Mobile).
 * 2. Injects the `AppWalkthrough` component for first-time user tours.
 * 3. Handles ergonomic safe-area-insets for mobile PWA usage.
 * 4. Provides user profile dropdown and logout functionality.
 */
export function MainLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { isConnected } = useEvents();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const handleLogout = () => {
    // logout() already handles navigation via window.location.href
    logout();
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const isActive = (href: string) =>
    href === '/' ? currentPath === '/' : currentPath.startsWith(href);

  return (
    <div className="flex min-h-[100dvh] w-full bg-slate-50 dark:bg-zinc-950 transition-all duration-300 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-50/50 via-slate-50 to-slate-100 dark:from-blue-950/20 dark:via-zinc-950 dark:to-zinc-950">
      {/* Onboarding tour — renders nothing, kicks off driver.js for first-time users */}
      <AppWalkthrough />

      {/* ── Desktop Sidebar ──────────────────────────────────── */}
      <aside
        id="tour-sidebar"
        className={cn(
          'fixed inset-y-0 left-0 z-50 hidden flex-col border-r border-white/20 bg-white/60 dark:bg-zinc-950/60 backdrop-blur-2xl sm:flex transition-all duration-300 ease-in-out shadow-2xl',
          isSidebarOpen ? 'w-64' : 'w-14'
        )}
      >
        <div className={cn('flex h-14 items-center border-b px-3 transition-all', isSidebarOpen ? 'justify-between' : 'justify-center')}>
          <Link to="/" className={cn('flex items-center gap-2 font-semibold transition-all overflow-hidden', isSidebarOpen ? 'w-auto' : 'w-0 opacity-0 hidden')}>
            <Package className="h-6 w-6" />
            <span className="whitespace-nowrap">Laundry POS</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className={cn('h-8 w-8 hover:bg-muted/50 rounded-full', isSidebarOpen ? '' : 'ml-0')}
            title={isSidebarOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
          >
            {isSidebarOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4">
          <ul className="grid gap-1 px-2">
            {getFilteredNavItems(navItems, user?.role).map((item) => (
              <li key={item.href}>
                <Link
                  to={item.href}
                  id={`tour-nav-${item.label.toLowerCase()}`}
                  activeProps={{ className: 'bg-primary/10 text-primary font-medium' }}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-2.5 py-2.5 text-muted-foreground transition-all hover:text-primary hover:bg-muted/50',
                    !isSidebarOpen && 'justify-center'
                  )}
                  title={!isSidebarOpen ? item.label : undefined}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <span
                    className={cn(
                      'transition-all duration-300 overflow-hidden whitespace-nowrap',
                      isSidebarOpen ? 'opacity-100 w-auto' : 'opacity-0 w-0 hidden'
                    )}
                  >
                    {item.label}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className={cn('border-t p-2 transition-all overflow-hidden', isSidebarOpen ? 'p-4' : 'p-2')}>
          <div className={cn('flex items-center gap-3 px-1 py-1 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer', !isSidebarOpen && 'justify-center')}>
            <Avatar className="h-8 w-8 border shrink-0">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-primary/10 text-primary font-bold text-xs">{user?.name?.charAt(0) || 'U'}</AvatarFallback>
            </Avatar>
            <div className={cn('flex flex-col transition-all overflow-hidden whitespace-nowrap', isSidebarOpen ? 'opacity-100 w-auto' : 'opacity-0 w-0 hidden')}>
              <span className="text-sm font-medium truncate max-w-[140px]">{user?.name}</span>
              <span className="text-[10px] text-muted-foreground capitalize font-mono tracking-wider">{user?.role}</span>
            </div>
          </div>
          {isSidebarOpen && (
            <Button variant="outline" size="sm" className="w-full mt-3 gap-2 text-xs h-8" onClick={handleLogout}>
              <LogOut className="h-3.5 w-3.5" />
              Logout
            </Button>
          )}
        </div>
      </aside>

      {/* ── Main content area ────────────────────────────────── */}
      <div className={cn('flex min-h-[100dvh] flex-col w-full transition-all duration-300 ease-in-out sm:gap-4 sm:py-4', isSidebarOpen ? 'sm:pl-64' : 'sm:pl-14')}>
        {/* Desktop-only slim header */}
        <header
          className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b border-white/20 bg-white/60 dark:bg-zinc-950/60 px-4 backdrop-blur-2xl sm:static sm:h-auto sm:border-0 sm:bg-transparent sm:px-6"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          {/* Brand on mobile */}
          <Link to="/" className="flex items-center gap-2 font-semibold sm:hidden">
            <Package className="h-6 w-6" />
            <span>Laundry POS</span>
          </Link>

          <div className="relative ml-auto flex-1 md:grow-0" />

          {/* SSE Connection Status */}
          <div 
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-medium transition-all duration-500 border",
              isConnected 
                ? "bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-800" 
                : "bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-800"
            )}
            title={isConnected ? "Real-time updates active" : "Real-time updates disconnected"}
          >
            <div className={cn("h-1.5 w-1.5 rounded-full", isConnected ? "bg-emerald-500 animate-pulse" : "bg-amber-500")} />
            <span className="hidden sm:inline">{isConnected ? "Live" : "Offline"}</span>
            {isConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
          </div>

          {/* Notifications Bell */}
          <NotificationBell />

          {/* User avatar dropdown — visible on all sizes */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button id="tour-user-menu" variant="secondary" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                </Avatar>
                <span className="sr-only">Toggle user menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate({ to: '/settings' })}>Settings</DropdownMenuItem>
              <DropdownMenuItem>Support</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content — extra bottom padding on mobile so the bar doesn't overlap */}
        <main className="flex w-full flex-1 flex-col items-stretch gap-4 p-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] sm:px-6 sm:py-0 sm:pb-0 md:gap-8">
          <Breadcrumbs />
          {children}
          <div
            className="w-full shrink-0 sm:hidden"
            style={{ height: 'calc(5.5rem + env(safe-area-inset-bottom))' }}
            aria-hidden="true"
          />
        </main>
      </div>

      {/* ── Mobile Bottom Navigation Bar ─────────────────────── */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 flex h-16 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:hidden"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0px)' }}
      >
        {/* Primary tabs */}
        {getFilteredNavItems(primaryMobileNav, user?.role).map((item) => {
          const active = isActive(item.href);
          return (
            <Link
              key={item.href}
              to={item.href}
              className="flex flex-1 flex-col items-center justify-center gap-1 py-2 min-w-0 relative transition-colors"
            >
              {/* Active indicator pill */}
              {active && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-full bg-primary" />
              )}
              <item.icon
                className={cn(
                  'h-5 w-5 shrink-0 transition-colors',
                  active ? 'text-primary' : 'text-muted-foreground'
                )}
              />
              <span
                className={cn(
                  'text-[10px] font-medium truncate transition-colors',
                  active ? 'text-primary' : 'text-muted-foreground'
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}

        {/* "More" tab — opens secondary sheet */}
        <Sheet open={isMoreOpen} onOpenChange={setIsMoreOpen}>
          <SheetTrigger asChild>
            <button
              className="flex flex-1 flex-col items-center justify-center gap-1 py-2 min-w-0 cursor-pointer"
            >
              <MoreHorizontal className="h-5 w-5 text-muted-foreground" />
              <span className="text-[10px] font-medium text-muted-foreground">More</span>
            </button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="max-h-[85dvh] overflow-y-auto rounded-t-2xl pb-6"
            style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
          >
            <div className="mx-auto mb-4 h-1 w-12 rounded-full bg-muted" />
            <nav className="grid gap-1">
              {getFilteredNavItems(secondaryMobileNav, user?.role).map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setIsMoreOpen(false)}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors hover:bg-muted',
                    isActive(item.href) ? 'text-primary bg-primary/10' : 'text-foreground'
                  )}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  {item.label}
                </Link>
              ))}

              {/* Divider + logout */}
              <div className="my-2 border-t" />
              <div className="flex items-center gap-3 rounded-lg px-4 py-3">
                <Avatar className="h-8 w-8 border">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="text-xs font-bold bg-primary/10 text-primary">{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-sm font-medium truncate">{user?.name}</span>
                  <span className="text-xs text-muted-foreground capitalize">{user?.role}</span>
                </div>
                <Button variant="ghost" size="sm" className="gap-2 text-destructive hover:text-destructive" onClick={() => { setIsMoreOpen(false); handleLogout(); }}>
                  <LogOut className="h-4 w-4" />
                  Logout
                </Button>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
}
